import { type FC, useRef } from 'react';

import { formatCost } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { formatDate } from 'utils/date';

import { BottomSheet, Drop, Link, Text, useBreakpoint, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';
import { ChevronDownOutlinedSize16, ChevronUpOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import Conversion from 'bloko/blocks/conversion';
import { formatToReactComponent } from 'bloko/common/trl';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { type BonusHistory, selectBonusHistory } from 'store/models/balance';
import { useSelector } from 'store/utils';

const BonusExpirationString: FC<BonusHistory[number] & { isFullStringFormat?: boolean }> = ({
    isFullStringFormat,
    expirationDate,
    sum,
}) => {
    const trl = useTrls();

    return formatToReactComponent(
        trl(`BalanceAndBonuses.BonusesCard.body.bonusesBurnOut.${isFullStringFormat ? 'full' : 'short'}`),
        {
            '[0]': formatCost(sum, NON_BREAKING_SPACE),
            '[1]': (
                <Conversion
                    zero={trl('BalanceAndBonuses.BonusesCard.bonuses.zero')}
                    one={trl('BalanceAndBonuses.BonusesCard.bonuses.one')}
                    some={trl('BalanceAndBonuses.BonusesCard.bonuses.some')}
                    many={trl('BalanceAndBonuses.BonusesCard.bonuses.many')}
                    value={sum}
                    hasValue={false}
                />
            ),
            '[2]': (
                <Conversion
                    zero={trl('BalanceAndBonuses.BonusesCard.burnOut.zero')}
                    one={trl('BalanceAndBonuses.BonusesCard.burnOut.one')}
                    some={trl('BalanceAndBonuses.BonusesCard.burnOut.some')}
                    many={trl('BalanceAndBonuses.BonusesCard.burnOut.many')}
                    value={sum}
                    hasValue={false}
                />
            ),
            '[3]': formatDate(new Date(expirationDate), 'd MMMM'),
        }
    );
};

const BonusExpirationList: FC = () => {
    const { isMobile } = useBreakpoint();

    return (
        <VSpacingContainer default={6}>
            {useSelector(selectBonusHistory).map(({ expirationDate, sum }) => (
                <Text typography={isMobile ? 'label-1-regular' : 'label-2-regular'} key={`${expirationDate},${sum}`}>
                    <BonusExpirationString isFullStringFormat expirationDate={expirationDate} sum={sum} />
                </Text>
            ))}
        </VSpacingContainer>
    );
};

const BonusExpirationInfo: FC = () => {
    const [isDropVisible, setIsDropVisibleOn, setIsDropVisibleOff] = useOnOffState(false);
    const bonusHistory = useSelector(selectBonusHistory);
    const twoOrMoreBonusHistoryRecords = bonusHistory.length > 1;
    const chevronIcon = isDropVisible ? <ChevronUpOutlinedSize16 /> : <ChevronDownOutlinedSize16 />;

    const linkWrapperRef = useRef(null);
    const { isMobile } = useBreakpoint();

    if (bonusHistory.length < 1) return null;

    return (
        <>
            {twoOrMoreBonusHistoryRecords ? (
                <>
                    <Link
                        ref={linkWrapperRef}
                        mode="primary"
                        Element="button"
                        onClick={isDropVisible ? setIsDropVisibleOff : setIsDropVisibleOn}
                        onMouseEnter={isMobile ? undefined : setIsDropVisibleOn}
                        onMouseLeave={isMobile ? undefined : setIsDropVisibleOff}
                        iconRight={twoOrMoreBonusHistoryRecords ? chevronIcon : undefined}
                        style="warning"
                        data-qa="bonus-expiration"
                    >
                        <BonusExpirationString {...bonusHistory[0]} isFullStringFormat={false} />
                    </Link>
                    <Drop
                        width={380}
                        onClose={setIsDropVisibleOff}
                        visible={isDropVisible}
                        activatorRef={linkWrapperRef}
                        placement="bottom-left"
                    >
                        <BonusExpirationList />
                    </Drop>

                    <BottomSheet withContentPaddings onClose={setIsDropVisibleOff} visible={isDropVisible} footer>
                        <BonusExpirationList />
                        <VSpacing default={8} />
                    </BottomSheet>
                </>
            ) : (
                <Text style="warning" typography="label-2-regular">
                    <BonusExpirationString {...bonusHistory[0]} isFullStringFormat={false} />
                </Text>
            )}
        </>
    );
};

export default BonusExpirationInfo;
